import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Download as DownloadIcon,
  LocalHospital as SurgeryIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

const SurgicalReports = () => {
  const [dateRange, setDateRange] = useState('30');
  const [reportType, setReportType] = useState('overview');

  const [surgeryData] = useState([
    { month: 'Jan', surgeries: 45, revenue: 125000, complications: 2, avgDuration: 3.2 },
    { month: 'Feb', surgeries: 52, revenue: 138000, complications: 1, avgDuration: 2.8 },
    { month: 'Mar', surgeries: 48, revenue: 132000, complications: 3, avgDuration: 3.1 },
    { month: 'Apr', surgeries: 61, revenue: 165000, complications: 2, avgDuration: 2.9 },
    { month: 'May', surgeries: 58, revenue: 158000, complications: 1, avgDuration: 3.0 },
    { month: 'Jun', surgeries: 65, revenue: 175000, complications: 2, avgDuration: 2.7 }
  ]);

  const [surgeryTypes] = useState([
    { name: 'Cardiac Surgery', count: 25, revenue: 450000, successRate: 98.5 },
    { name: 'Orthopedic Surgery', count: 42, revenue: 380000, successRate: 97.8 },
    { name: 'General Surgery', count: 38, revenue: 220000, successRate: 99.1 },
    { name: 'Neurosurgery', count: 15, revenue: 320000, successRate: 96.5 },
    { name: 'Ophthalmology', count: 28, revenue: 180000, successRate: 99.5 }
  ]);

  const [roomUtilization] = useState([
    { room: 'OR-1', utilization: 85, surgeries: 45, revenue: 280000 },
    { room: 'OR-2', utilization: 78, surgeries: 38, revenue: 220000 },
    { room: 'OR-3', utilization: 92, surgeries: 52, revenue: 310000 },
    { room: 'OR-4', utilization: 65, surgeries: 28, revenue: 180000 },
    { room: 'OR-5', utilization: 88, surgeries: 42, revenue: 250000 }
  ]);

  const [teamPerformance] = useState([
    { team: 'Cardiac Team A', surgeries: 25, successRate: 98.5, avgDuration: 4.2 },
    { team: 'Orthopedic Team', surgeries: 42, successRate: 97.8, avgDuration: 3.1 },
    { team: 'General Surgery Team', surgeries: 38, successRate: 99.1, avgDuration: 2.8 },
    { team: 'Neurosurgery Team', surgeries: 15, successRate: 96.5, avgDuration: 5.5 },
    { team: 'Ophthalmology Team', surgeries: 28, successRate: 99.5, avgDuration: 1.2 }
  ]);

  const [equipmentStatus] = useState([
    { name: 'Operational', value: 85, color: '#4caf50' },
    { name: 'Maintenance', value: 10, color: '#ff9800' },
    { name: 'Out of Service', value: 5, color: '#f44336' }
  ]);

  const StatCard = ({ title, value, icon, color, trend, subtitle }) => (
    <Card sx={{ 
      height: '100%', 
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}30`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 25px ${color}20`,
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>
            {icon}
          </Avatar>
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {trend > 0 ? <TrendingUpIcon color="success" /> : <TrendingDownIcon color="error" />}
              <Typography variant="caption" color={trend > 0 ? 'success.main' : 'error.main'}>
                {Math.abs(trend)}%
              </Typography>
            </Box>
          )}
        </Box>
        <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const handleDownloadReport = (type) => {
    // Mock download functionality
    console.log(`Downloading ${type} report...`);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            Surgical Reports & Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive analytics and reporting for surgical facilities
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="365">Last year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownloadReport('comprehensive')}
            sx={{ borderRadius: 2 }}
          >
            Export Report
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Surgeries"
            value="548"
            icon={<SurgeryIcon />}
            color="#708bff"
            trend={12}
            subtitle="Last 6 months"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value="$1.2M"
            icon={<MoneyIcon />}
            color="#4caf50"
            trend={8}
            subtitle="Last 6 months"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Success Rate"
            value="98.2%"
            icon={<CheckCircleIcon />}
            color="#ff9800"
            trend={2}
            subtitle="Average"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Duration"
            value="3.1h"
            icon={<ScheduleIcon />}
            color="#9c27b0"
            trend={-5}
            subtitle="Per surgery"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Surgery Trends Chart */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Surgery Trends (Last 6 Months)
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadReport('trends')}
              >
                Export
              </Button>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={surgeryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <RechartsTooltip />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="surgeries"
                  stroke="#708bff"
                  strokeWidth={3}
                  dot={{ fill: '#708bff', strokeWidth: 2, r: 4 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="revenue"
                  stroke="#4caf50"
                  strokeWidth={3}
                  dot={{ fill: '#4caf50', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Equipment Status */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 3 }}>
              Equipment Status
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={equipmentStatus}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {equipmentStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 2 }}>
              {equipmentStatus.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.value}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Surgery Types Performance */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Surgery Types Performance
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadReport('surgery-types')}
              >
                Export
              </Button>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={surgeryTypes}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <RechartsTooltip />
                <Bar dataKey="count" fill="#708bff" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Room Utilization */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Room Utilization
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadReport('room-utilization')}
              >
                Export
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Room</TableCell>
                    <TableCell align="right">Utilization</TableCell>
                    <TableCell align="right">Surgeries</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roomUtilization.map((room) => (
                    <TableRow key={room.room}>
                      <TableCell>{room.room}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${room.utilization}%`}
                          size="small"
                          color={room.utilization > 80 ? 'success' : room.utilization > 60 ? 'warning' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="right">{room.surgeries}</TableCell>
                      <TableCell align="right">${room.revenue.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Team Performance */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Team Performance
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadReport('team-performance')}
              >
                Export
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Team</TableCell>
                    <TableCell align="right">Surgeries</TableCell>
                    <TableCell align="right">Success Rate</TableCell>
                    <TableCell align="right">Avg Duration</TableCell>
                    <TableCell align="right">Performance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamPerformance.map((team) => (
                    <TableRow key={team.team}>
                      <TableCell>{team.team}</TableCell>
                      <TableCell align="right">{team.surgeries}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${team.successRate}%`}
                          size="small"
                          color={team.successRate > 98 ? 'success' : team.successRate > 95 ? 'warning' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="right">{team.avgDuration}h</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={team.successRate > 98 ? 'Excellent' : team.successRate > 95 ? 'Good' : 'Needs Improvement'}
                          size="small"
                          color={team.successRate > 98 ? 'success' : team.successRate > 95 ? 'warning' : 'error'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SurgicalReports; 